import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { FilePen, CheckCircle2 } from 'lucide-react';
import { Card } from '@components/base/layout';
import { actionAnchorId } from '../configs/otherActionsConfig';
import { Text, Icon } from '@components/base/display';
import { Button } from '@components/base/actions';
import { EntityForm, useEntityForm } from '@components/integrated/entity-form';
import type {
  EntityFormConfig,
  SelectFieldOptions,
  DateFieldOptions,
  TimeFieldOptions,
  TextareaOptions,
  TextFieldOptions,
  CheckboxFieldOptions,
} from '@components/integrated/entity-form';
import {
  applyLayoutOverrides,
  buildPublishedConfigFromEntityForm,
  resolveCardTitle,
  usePublishLayoutConfig,
} from '@components/shared/configurable';
import type { LayoutOverrides } from '@components/shared/configurable';
import { claimflowApiClient } from '../api/claimflowApiClient';

interface MiseAJourDossierCardProps {
  ses: string;
  idSin: string;
  /**
   * Per-tenant field layout overrides injected by the `withComponentRules`
   * HOC. Underscored to match the injected meta-prop convention; `undefined`
   * at baseline ⇒ the form config is returned unchanged (byte-identical
   * render). Because every field now lives in `EntityFormConfig.fields` with
   * an explicit `order`, a `reorderFields` rule can change the rendered field
   * order without any JSX edit (the previous hand-placed grid froze it).
   */
  _layoutOverrides?: LayoutOverrides;
}

interface ErpProxyResponse<T> {
  success: boolean;
  data: { total: number; data: T[] };
  error?: { code: string; message: string } | null;
}

/** Wrapper shape for the non-paginated `sinistre-inline-get` proxy call. */
interface SinistreFluxResponse {
  success: boolean;
  data: SinistreFlux | { total: number; data: SinistreFlux[] };
  error?: { code: string; message: string } | null;
}

/**
 * The claim "flux" object. Only the fields we read/write are typed; everything
 * else (listPer, pmq, etapes, …) rides through verbatim via the index
 * signature so a read-modify-write PUT preserves untouched data.
 */
interface SinistreFlux {
  motif?: string;
  type?: string;
  txResp?: string;
  circonstance?: string;
  causeCirconstance?: string;
  dateSurvenance?: string;
  heureSurvenance?: string;
  dommages?: string;
  risque3?: string;
  risque4?: string;
  risque5?: string;
  cdIda?: string;
  infos?: Record<string, unknown>;
  [key: string]: unknown;
}

interface CdMotRow {
  CD_MOT: string;
  LIB_MOT: string;
}

interface CodeLibRow {
  CODE: string;
  LIB: string;
}

interface CdCircRow {
  CD_CIRC: string;
  LIB_CIRC: string;
}

interface CdIdaRow {
  CD_IDA: string;
  LIB_IDA: string;
}

interface Gestion2Row {
  CODE: string;
  VALEUR: string;
}

/**
 * Static country list. No shared country registry exists in the codebase, so a
 * small list (France first plus common neighbours) lives here. Values are
 * ISO-3166 alpha-2 codes, matching the `infos.PAYSSURV` contract.
 */
const PAYS_ITEMS = [
  { value: 'FR', label: 'France' },
  { value: 'BE', label: 'Belgique' },
  { value: 'CH', label: 'Suisse' },
  { value: 'DE', label: 'Allemagne' },
  { value: 'ES', label: 'Espagne' },
  { value: 'IT', label: 'Italie' },
  { value: 'LU', label: 'Luxembourg' },
  { value: 'GB', label: 'Royaume-Uni' },
];

const MENEUR_ITEMS = [
  { value: '1', label: 'Oui' },
  { value: '0', label: 'Non' },
];

function pad2(s: string): string {
  return s.padStart(2, '0');
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function formatDdMmYyyy(d: Date): string {
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${String(d.getFullYear())}`;
}

/** Parse a `dd/MM/yyyy` string into a Date, or null if not parseable. */
function parseDdMmYyyy(s: string | undefined): Date | null {
  if (!s) return null;
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(s.trim());
  if (!m) return null;
  const day = Number(m[1]);
  const month = Number(m[2]);
  const year = Number(m[3]);
  const d = new Date(year, month - 1, day);
  if (Number.isNaN(d.getTime())) return null;
  return d;
}

/**
 * The flux stores the hour as a 2-digit string (e.g. "08"). The TimePicker
 * works in `HH:mm`, so display it as "08:00".
 */
function hourToHHmm(h: string | undefined): string | null {
  if (!h) return null;
  const trimmed = h.trim();
  if (!trimmed) return null;
  if (/^\d{1,2}$/.test(trimmed)) return `${pad2(trimmed)}:00`;
  if (/^\d{1,2}:\d{2}$/.test(trimmed)) {
    const [hh = '', mm = '00'] = trimmed.split(':');
    return `${pad2(hh)}:${mm}`;
  }
  return null;
}

/** Convert a `HH:mm` value back to the 2-digit hour string the flux expects. */
function hhmmToHour(v: string | null): string {
  if (!v) return '';
  const [hh] = v.split(':');
  return pad2(hh ?? '');
}

/** Coerce an unknown flux value to a string (empty for null/undefined). */
function str(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return '';
}

function boolToFlag(v: unknown): string {
  return v === true ? '1' : '0';
}

function flagToBool(v: unknown): boolean {
  return str(v) === '1';
}

function extractServerMessage(err: unknown): string | undefined {
  if (typeof err !== 'object' || err === null) return undefined;
  const response = (err as { response?: { data?: unknown } }).response;
  const data = response?.data;
  if (typeof data !== 'object' || data === null) return undefined;
  const root = data as Record<string, unknown>;
  if (typeof root.message === 'string' && root.message.length > 0) {
    return root.message;
  }
  const inner = root.data;
  if (typeof inner === 'object' && inner !== null) {
    const innerMsg = (inner as Record<string, unknown>).message;
    if (typeof innerMsg === 'string' && innerMsg.length > 0) return innerMsg;
  }
  const error = root.error;
  if (typeof error === 'object' && error !== null) {
    const m = (error as Record<string, unknown>).message;
    if (typeof m === 'string' && m.length > 0) return m;
  }
  return undefined;
}

/**
 * Unwrap the `sinistre-inline-get` proxy response. The endpoint is
 * non-paginated, so the flux lives at `response.data` — but defensively handle
 * the `{ total, data: [flux] }` envelope used by the list endpoints too.
 */
function unwrapFlux(payload: SinistreFluxResponse): SinistreFlux | null {
  const data: unknown = payload.data;
  if (typeof data !== 'object' || data === null) return null;
  const maybeEnvelope = data as { data?: unknown };
  if (Array.isArray(maybeEnvelope.data)) {
    const [first] = maybeEnvelope.data as SinistreFlux[];
    return first ?? null;
  }
  return data as SinistreFlux;
}

export function MiseAJourDossierCard({
  ses,
  idSin,
  _layoutOverrides,
}: Readonly<MiseAJourDossierCardProps>) {
  const [submitted, setSubmitted] = useState(false);
  // Collapsed on first paint — every card in the claim view's right column
  // opens folded.
  const [expanded, setExpanded] = useState(false);

  const [motRows, setMotRows] = useState<CdMotRow[]>([]);
  const [respRows, setRespRows] = useState<CodeLibRow[]>([]);
  const [typeRows, setTypeRows] = useState<CodeLibRow[]>([]);
  const [circRows, setCircRows] = useState<CdCircRow[]>([]);
  const [gestionRows, setGestionRows] = useState<CodeLibRow[]>([]);
  const [gestion2Rows, setGestion2Rows] = useState<Gestion2Row[]>([]);
  const [recoursRows, setRecoursRows] = useState<CodeLibRow[]>([]);
  const [idaRows, setIdaRows] = useState<CdIdaRow[]>([]);

  const fluxRef = useRef<SinistreFlux | null>(null);
  const gestion2ReqIdRef = useRef(0);
  const prefilledRef = useRef(false);
  const prevGestionRef = useRef<string>('');
  // Tracks the last gestion code we actually fired a gestion2 fetch for, so the
  // cascade effect can no-op when it re-runs for an unrelated reason (see below).
  const lastFetchedGestionRef = useRef<string>('');

  // Static option fetches on mount
  useEffect(() => {
    let cancelled = false;

    async function fetchList(
      slug: string,
      setter: (rows: unknown[]) => void,
      extraParams?: Record<string, unknown>,
    ) {
      try {
        const r = await claimflowApiClient.get<ErpProxyResponse<unknown>>(
          `/claimflow/erp/registries/veos/endpoints/${slug}`,
          { params: { SES: ses, perPage: 9999, ...extraParams } },
        );
        if (!cancelled && r.data.success) setter(r.data.data.data);
      } catch {
        /* swallow — options stay empty */
      }
    }

    void fetchList(
      'boby-ws2-gs-sel-cdmot',
      (rows) => {
        setMotRows(rows as CdMotRow[]);
      },
      { id_sin: idSin },
    );
    void fetchList('boby-ws2-gs-sel-txrespsin', (rows) => {
      setRespRows(rows as CodeLibRow[]);
    });
    void fetchList('boby-ws2-gs-sel-typsin', (rows) => {
      setTypeRows(rows as CodeLibRow[]);
    });
    void fetchList('boby-ws2-gs-sel-cdcirc', (rows) => {
      setCircRows(rows as CdCircRow[]);
    });
    void fetchList('boby-ws2-gs-sel-gestion', (rows) => {
      setGestionRows(rows as CodeLibRow[]);
    });
    void fetchList('boby-ws2-gs-sel-recours', (rows) => {
      setRecoursRows(rows as CodeLibRow[]);
    });
    void fetchList('boby-ws2-gs-sel-cdida', (rows) => {
      setIdaRows(rows as CdIdaRow[]);
    });

    return () => {
      cancelled = true;
    };
  }, [ses, idSin]);

  const fetchGestion2 = useCallback(
    async (gestion: string) => {
      const reqId = ++gestion2ReqIdRef.current;
      try {
        const r = await claimflowApiClient.get<ErpProxyResponse<Gestion2Row>>(
          '/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-sel-gestion2',
          { params: { SES: ses, gestion, perPage: 9999 } },
        );
        if (reqId !== gestion2ReqIdRef.current) return;
        if (r.data.success) setGestion2Rows(r.data.data.data);
      } catch {
        /* swallow */
      }
    },
    [ses],
  );

  const motItems = useMemo(
    () => motRows.map((r) => ({ value: r.CD_MOT, label: r.LIB_MOT })),
    [motRows],
  );
  const respItems = useMemo(
    () => respRows.map((r) => ({ value: r.CODE, label: r.LIB })),
    [respRows],
  );
  const typeItems = useMemo(
    () => typeRows.map((r) => ({ value: r.CODE, label: r.LIB })),
    [typeRows],
  );
  const circItems = useMemo(
    () => circRows.map((r) => ({ value: r.CD_CIRC, label: r.LIB_CIRC })),
    [circRows],
  );
  const gestionItems = useMemo(
    () => gestionRows.map((r) => ({ value: r.CODE, label: r.LIB })),
    [gestionRows],
  );
  const gestion2Items = useMemo(
    () => gestion2Rows.map((r) => ({ value: r.CODE, label: r.VALEUR })),
    [gestion2Rows],
  );
  const recoursItems = useMemo(
    () => recoursRows.map((r) => ({ value: r.CODE, label: r.LIB })),
    [recoursRows],
  );
  const idaItems = useMemo(
    () =>
      idaRows.map((r) => ({
        value: r.CD_IDA,
        label: `${r.CD_IDA} / ${r.LIB_IDA}`,
      })),
    [idaRows],
  );

  const mutationFn = useCallback(
    async (payload: Record<string, unknown> | FormData) => {
      const data =
        payload instanceof FormData
          ? Object.fromEntries(payload.entries())
          : payload;

      const flux = fluxRef.current;
      if (!flux) {
        throw new Error('Le dossier n’a pas pu être chargé.');
      }

      // Read-modify-write: clone the latest flux, overwrite ONLY mapped fields,
      // and preserve everything else (listPer, pmq, etapes, …) verbatim.
      const next: SinistreFlux = { ...flux };
      const infos: Record<string, unknown> = { ...(flux.infos ?? {}) };

      const survenanceDate =
        data.dateSurvenance instanceof Date ? data.dateSurvenance : null;

      const setField = (key: keyof SinistreFlux, value: unknown) => {
        next[key] = str(value);
      };
      setField('motif', data.statut);
      setField('type', data.type);
      setField('txResp', data.txResp);
      setField('circonstance', data.circonstance);
      setField('causeCirconstance', data.cause);
      setField('dommages', data.dommages);
      setField('risque3', data.adresse);
      setField('risque4', data.codePostal);
      setField('risque5', data.ville);
      setField('cdIda', data.cdIda);
      next.dateSurvenance = survenanceDate
        ? formatDdMmYyyy(survenanceDate)
        : '';
      // The hour now lives in the EntityForm config (`heureSurvenance` field,
      // a `time` field holding a `HH:mm` string). Read it off the submitted
      // form values and convert back to the bespoke 2-digit string the flux
      // expects (`hhmmToHour` maps null/empty/HH:mm → "" or "HH"), so the flux
      // payload stays byte-identical to the old hand-placed TimePicker path.
      next.heureSurvenance = hhmmToHour(
        typeof data.heureSurvenance === 'string' ? data.heureSurvenance : null,
      );

      const setInfo = (key: string, value: unknown) => {
        infos[key] = str(value);
      };
      setInfo('GESTION', data.gestion);
      setInfo('GESTION2', data.gestion2);
      setInfo('MJ', data.meneurJeu);
      setInfo('RECLAH', data.recoursHdi);
      setInfo('RECLAT', data.recoursTiers);
      setInfo('ARGOS', boolToFlag(data.argos));
      setInfo('CESSIONVEH', boolToFlag(data.cessionVeh));
      setInfo('PAYSSURV', data.pays);
      setInfo('DESC', data.circonstancesDetaillees);
      setInfo('VAL_INFS', data.observations);
      setInfo('CTX', boolToFlag(data.contentieux));
      setInfo('DOSSINV', boolToFlag(data.aInvestiguer));
      next.infos = infos;

      try {
        const response = await claimflowApiClient.put<{ success: boolean }>(
          '/claimflow/erp/registries/veos/endpoints/sinistre-update',
          // `id_sin` must live INSIDE `data`: the Laravel proxy builds the engine
          // input from `array_merge($request->query(), $request->input('data'))`,
          // so a top-level sibling of `data` is silently dropped. The execution
          // engine extracts `id_sin` from the data to populate the
          // `PUT /sinistre/{id_sin}` path param (and strips it from the outgoing
          // body), so the stored baseline `next` deliberately keeps no id_sin.
          { data: { ...next, id_sin: idSin } },
        );
        // Keep the freshly-saved flux as the new baseline for any later edits.
        fluxRef.current = next;
        return response.data;
      } catch (err) {
        const serverMessage = extractServerMessage(err);
        if (serverMessage) throw new Error(serverMessage);
        throw err;
      }
    },
    [idSin],
  );

  const baseConfig: EntityFormConfig = useMemo(
    () => ({
          id: 'mise-a-jour-dossier',
          endpoint: {
            erpEndpoint:
              '/claimflow/erp/registries/veos/endpoints/sinistre-update',
            method: 'PUT',
          },
          // Every field carries an explicit `order` (1→25) reproducing the
          // historic hand-placed grid top-to-bottom, plus `colSpan` for the
          // two-column flow. The render below maps the config sorted by
          // `order`, so a `reorderFields` layout rule changes the rendered
          // order with no JSX edit. "Heure de survenance" is now the
          // `heureSurvenance` `time` field (order 5), folded into the config so
          // it is rename/hide/reorder-configurable like every other field.
          fields: {
            statut: {
              type: 'select',
              label: 'Statut',
              order: 1,
              colSpan: 2,
              options: {
                items: motItems,
                searchable: true,
                placeholder: 'Sélectionner un statut',
              } as SelectFieldOptions,
            },
            contentieux: {
              type: 'checkbox',
              label: '',
              order: 2,
              options: {
                checkboxLabel: 'Contentieux',
              } as CheckboxFieldOptions,
            },
            aInvestiguer: {
              type: 'checkbox',
              label: '',
              order: 3,
              options: {
                checkboxLabel: 'À investiguer',
              } as CheckboxFieldOptions,
            },
            dateSurvenance: {
              type: 'date',
              label: 'Date de survenance',
              order: 4,
              placeholder: 'jj/mm/aaaa',
              options: { displayFormat: 'dd/MM/yyyy' } as DateFieldOptions,
            },
            // Folded the former hand-placed `<TimePicker label="Heure de
            // survenance">` into the declarative config so its label /
            // placeholder / visibility / order are tenant-layout-configurable
            // (rename / hide / reorder via the rule engine) like every other
            // field. The `time` type maps to the same base TimePicker; the
            // value is a `HH:mm` string, converted back to the flux's 2-digit
            // hour by `hhmmToHour` at submit. Holds the historic order-5 slot
            // (right after `dateSurvenance`), so it now reorders with the form.
            heureSurvenance: {
              type: 'time',
              label: 'Heure de survenance',
              order: 5,
              placeholder: '--:--',
              options: {} as TimeFieldOptions,
            },
            txResp: {
              type: 'select',
              label: 'Taux de responsabilité',
              order: 6,
              options: {
                items: respItems,
                searchable: true,
                placeholder: 'Sélectionner un taux de responsabilité',
              } as SelectFieldOptions,
            },
            type: {
              type: 'select',
              label: 'Type',
              order: 7,
              options: {
                items: typeItems,
                searchable: true,
                placeholder: 'Sélectionner un type',
              } as SelectFieldOptions,
            },
            circonstance: {
              type: 'select',
              label: 'Circonstances',
              order: 8,
              colSpan: 2,
              options: {
                items: circItems,
                searchable: true,
                placeholder: 'Sélectionner une circonstance',
              } as SelectFieldOptions,
            },
            gestion: {
              type: 'select',
              label: 'Gestion',
              order: 9,
              options: {
                items: gestionItems,
                searchable: true,
                placeholder: 'Sélectionner une gestion',
              } as SelectFieldOptions,
            },
            gestion2: {
              type: 'select',
              label: 'Gestion 2',
              order: 10,
              options: {
                items: gestion2Items,
                searchable: true,
                placeholder: 'Sélectionner d’abord une gestion',
              } as SelectFieldOptions,
            },
            adresse: {
              type: 'text',
              label: 'Adresse',
              order: 11,
              colSpan: 2,
              options: {} as TextFieldOptions,
            },
            codePostal: {
              type: 'text',
              label: 'Code postal',
              order: 12,
              options: { maxLength: 10 } as TextFieldOptions,
            },
            ville: {
              type: 'text',
              label: 'Ville',
              order: 13,
              options: {} as TextFieldOptions,
            },
            pays: {
              type: 'select',
              label: 'Pays',
              order: 14,
              colSpan: 2,
              options: {
                items: PAYS_ITEMS,
                searchable: true,
                placeholder: 'Sélectionner un pays',
              } as SelectFieldOptions,
            },
            argos: {
              type: 'checkbox',
              label: '',
              order: 15,
              colSpan: 2,
              options: {
                checkboxLabel: 'Info à Argos',
              } as CheckboxFieldOptions,
            },
            recoursTiers: {
              type: 'select',
              label: 'Recours Tiers',
              order: 16,
              options: {
                items: recoursItems,
                searchable: true,
                placeholder: 'Sélectionner un recours tiers',
              } as SelectFieldOptions,
            },
            recoursHdi: {
              type: 'select',
              label: 'Recours HDI',
              order: 17,
              options: {
                items: recoursItems,
                searchable: true,
                placeholder: 'Sélectionner un recours HDI',
              } as SelectFieldOptions,
            },
            meneurJeu: {
              type: 'select',
              label: 'Meneur de jeu',
              order: 18,
              options: {
                items: MENEUR_ITEMS,
                placeholder: 'Sélectionner un meneur de jeu',
              } as SelectFieldOptions,
            },
            cessionVeh: {
              type: 'checkbox',
              label: '',
              order: 19,
              options: {
                checkboxLabel: 'Cession véhicule',
              } as CheckboxFieldOptions,
            },
            cdIda: {
              type: 'select',
              label: 'Cas de barème',
              order: 20,
              colSpan: 2,
              options: {
                items: idaItems,
                searchable: true,
                placeholder: 'Sélectionner un cas de barème',
              } as SelectFieldOptions,
            },
            observations: {
              type: 'textarea',
              label: 'Observations',
              order: 21,
              colSpan: 2,
              options: { rows: 3 } as TextareaOptions,
            },
            cause: {
              type: 'textarea',
              label: 'Cause',
              order: 22,
              colSpan: 2,
              placeholder: 'Cause du sinistre',
              options: { rows: 3 } as TextareaOptions,
            },
            dommages: {
              type: 'textarea',
              label: 'Dommages',
              order: 23,
              colSpan: 2,
              placeholder: 'Dommages constatés',
              options: { rows: 3 } as TextareaOptions,
            },
            circonstancesDetaillees: {
              type: 'textarea',
              label: 'Circonstances détaillées',
              order: 24,
              colSpan: 2,
              options: { rows: 3 } as TextareaOptions,
            },
          },
          actions: {
            submit: { visible: true, label: 'Enregistrer les modifications' },
            cancel: { visible: false },
            reset: { visible: false },
          },
          layout: { columns: 2, gap: 'sm' },
        }),
    [
      motItems,
      respItems,
      typeItems,
      circItems,
      gestionItems,
      gestion2Items,
      recoursItems,
      idaItems,
    ],
  );

  usePublishLayoutConfig(
    'claimflow.miseAJourDossierCard',
    buildPublishedConfigFromEntityForm(baseConfig, 'Mise à jour dossier'),
  );

  const formConfig: EntityFormConfig = useMemo(
    () => applyLayoutOverrides(baseConfig, _layoutOverrides),
    [baseConfig, _layoutOverrides],
  );

  const handleSuccess = useCallback(() => {
    setSubmitted(true);
    // Collapse the card on success. The Card only reads `defaultExpanded` once
    // (it is uncontrolled), so the render below keys the Card on `submitted` to
    // force a remount that picks up `defaultExpanded={expanded}` with the new
    // collapsed value. The form lives in `useEntityForm` (outside the Card), so
    // the remount resets only the Card's collapse state, not the form values.
    setExpanded(false);
  }, []);

  const form = useEntityForm({
    config: formConfig,
    onSuccess: handleSuccess,
    mutationFn,
  });

  const { setValues } = form;

  // Prefill from the full claim flux on mount.
  useEffect(() => {
    let cancelled = false;
    async function loadFlux() {
      try {
        const r = await claimflowApiClient.get<SinistreFluxResponse>(
          '/claimflow/erp/registries/veos/endpoints/sinistre-inline-get',
          { params: { SES: ses, id_sin: idSin, inline: 1 } },
        );
        if (cancelled || !r.data.success) return;
        const flux = unwrapFlux(r.data);
        if (!flux) return;
        fluxRef.current = flux;
        if (prefilledRef.current) return;
        prefilledRef.current = true;

        const infos = flux.infos ?? {};

        // Seed the gestion guard with the prefilled value so the
        // "clear gestion2 on gestion change" effect does not treat the
        // prefill itself as a user-driven change and wipe gestion2.
        prevGestionRef.current = str(infos.GESTION);

        setValues({
          statut: flux.motif ?? '',
          type: flux.type ?? '',
          txResp: flux.txResp ?? '',
          circonstance: flux.circonstance ?? '',
          cause: flux.causeCirconstance ?? '',
          dommages: flux.dommages ?? '',
          adresse: flux.risque3 ?? '',
          codePostal: flux.risque4 ?? '',
          ville: flux.risque5 ?? '',
          cdIda: flux.cdIda ?? '',
          dateSurvenance: parseDdMmYyyy(flux.dateSurvenance),
          // Seed the folded `heureSurvenance` time field as a `HH:mm` string
          // (empty when the flux carries no/garbage hour). `hourToHHmm` maps
          // the flux's bespoke 2-digit hour ("08") to the picker's "08:00".
          heureSurvenance: hourToHHmm(flux.heureSurvenance) ?? '',
          gestion: str(infos.GESTION),
          gestion2: str(infos.GESTION2),
          meneurJeu: str(infos.MJ),
          recoursHdi: str(infos.RECLAH),
          recoursTiers: str(infos.RECLAT),
          argos: flagToBool(infos.ARGOS),
          cessionVeh: flagToBool(infos.CESSIONVEH),
          pays: str(infos.PAYSSURV),
          circonstancesDetaillees: str(infos.DESC),
          observations: str(infos.VAL_INFS),
          contentieux: flagToBool(infos.CTX),
          aInvestiguer: flagToBool(infos.DOSSINV),
        });
      } catch {
        /* swallow — prefill stays empty */
      }
    }
    void loadFlux();
    return () => {
      cancelled = true;
    };
  }, [ses, idSin, setValues]);

  const gestionValue = form.getValue('gestion');

  // Cascade: gestion -> gestion2.
  //
  // - On a user-driven gestion change, clear the previously-selected gestion2
  //   value. The prefill seeds `prevGestionRef` with the loaded gestion, so the
  //   initial prefill (which sets gestion + gestion2 together) is not mistaken
  //   for a user change.
  // - Refetch the gestion2 options for the new gestion. The fetch is scheduled
  //   off the synchronous effect body (its state updates land asynchronously),
  //   so it does not trigger a cascading-render warning.
  //
  // GUARD: `lastFetchedGestionRef` records the gestion we last fetched for, so
  // repeat runs for the same code become no-ops. `useEntityForm` now memoises
  // its return value, so `form` is referentially stable and this effect no
  // longer re-runs on every render — but the guard stays as defense in depth
  // against a future regression (or an unmemoised consumer) re-arming the
  // infinite gestion2 fetch loop.
  useEffect(() => {
    const code = str(gestionValue);
    const changed = code !== prevGestionRef.current;
    if (changed) {
      prevGestionRef.current = code;
      if (form.getValue('gestion2')) form.setValue('gestion2', '');
    }
    if (!code) {
      gestion2ReqIdRef.current++;
      // Allow re-selecting the same gestion after clearing to refetch.
      lastFetchedGestionRef.current = '';
      return;
    }
    if (code === lastFetchedGestionRef.current) return;
    lastFetchedGestionRef.current = code;
    queueMicrotask(() => {
      void fetchGestion2(code);
    });
  }, [gestionValue, fetchGestion2, form]);

  const gestion2Disabled = !gestionValue;

  // Auto-render the fields in config `order` (not JSX source order), so a
  // `reorderFields` layout rule reorders them with no JSX edit. The folded
  // "Heure de survenance" (`heureSurvenance`, a `time` field) now participates
  // in this sort like every other field — it is disabled once the dossier is
  // submitted (parity with the old hand-placed TimePicker). `gestion2` keeps
  // its runtime disabled/options overrides (driven by the gestion cascade).
  const fieldElements = useMemo(() => {
    const sorted = Object.entries(formConfig.fields).sort(
      (a, b) =>
        (a[1].order ?? Number.MAX_SAFE_INTEGER) -
        (b[1].order ?? Number.MAX_SAFE_INTEGER),
    );
    const elements: ReactNode[] = [];
    for (const [name] of sorted) {
      if (name === 'gestion2') {
        elements.push(
          <EntityForm.Field
            key="gestion2"
            name="gestion2"
            overrides={{
              disabled: gestion2Disabled,
              options: {
                items: gestion2Items,
                searchable: true,
                placeholder: gestion2Disabled
                  ? 'Sélectionner d’abord une gestion'
                  : 'Sélectionner',
              } as SelectFieldOptions,
            }}
          />,
        );
      } else if (name === 'heureSurvenance') {
        elements.push(
          <EntityForm.Field
            key="heureSurvenance"
            name="heureSurvenance"
            overrides={{ disabled: submitted }}
          />,
        );
      } else {
        elements.push(<EntityForm.Field key={name} name={name} />);
      }
    }
    return elements;
  }, [formConfig.fields, gestion2Disabled, gestion2Items, submitted]);

  const headerTitle = (
    <Text as="span" weight="semibold" size="base">
      {resolveCardTitle('Mise à jour dossier', _layoutOverrides)}
    </Text>
  );

  return (
    // Key on `submitted` so the Card remounts when the submit succeeds, letting
    // the uncontrolled Card re-read `defaultExpanded={expanded}` (now false) and
    // render collapsed. The user can still re-expand it manually afterwards.
    <Card key={submitted ? 'submitted' : 'editing'} id={actionAnchorId('claimflow.miseAJourDossierCard')}>
      <Card.Header
        collapsible
        defaultExpanded={expanded}
        onExpandChange={setExpanded}
        action={
          submitted ? (
            <Icon icon={CheckCircle2} size="md" color="success" />
          ) : undefined
        }
      >
        <Icon icon={FilePen} size="md" color="primary" />
        {headerTitle}
        {submitted && (
          <Text as="span" size="sm" color="muted" className="ml-2">
            (Dossier mis à jour)
          </Text>
        )}
      </Card.Header>
      <Card.Content>
        <EntityForm config={formConfig} form={form}>
          {/* Only surface the error alert: a successful submit collapses the
              card (see Card.Header below), so the EntityForm default "success"
              alert must never render. Error alerts keep working. */}
          {form.formError && <EntityForm.Alert />}
          {/*
            Auto-rendered from `formConfig.fields` sorted by `order` (see
            `fieldElements`). Each `<EntityForm.Field>` carries its own
            `col-span-*` wrapper from `field.colSpan`, so the grid reproduces
            the historic two-column layout. A `reorderFields` rule changes the
            sort and thus the rendered order with no JSX edit.
          */}
          <div className="grid grid-cols-2 gap-3">{fieldElements}</div>
          {formConfig.actions?.submit?.visible !== false && (
            <div className="pt-4">
              <Button
                type="submit"
                variant="primary"
                leftIcon={<Icon icon={FilePen} size="sm" />}
                loading={form.isSubmitting}
                disabled={form.isSubmitting || submitted}
                className="w-full"
              >
                {submitted
                  ? 'Dossier mis à jour'
                  : (formConfig.actions?.submit?.label ??
                    'Enregistrer les modifications')}
              </Button>
            </div>
          )}
        </EntityForm>
      </Card.Content>
    </Card>
  );
}
